'use client';

import { useState, useMemo } from 'react';
import { Button, Input, User } from '@heroui/react';
import { useScheduler, AdditionalContactSelection } from '../SchedulerContext';
import { ContactRoleType, ContactType } from '@/util/types/serverTypes';
import { ContactFormData } from '../SchedulerContext';
import { callServerAction } from '@/util/functions/fetching/callServerAction';
import { FaEnvelope, FaPlus, FaTrash, FaArrowRight } from 'react-icons/fa6';
import * as Icons from 'react-icons/fa6';
import { z } from 'zod';
import { parsePhoneNumber } from 'libphonenumber-js';
import ContactQuickForm from './ContactQuickForm';
import { formatPhoneForDisplay } from '@/util/functions/formatPhoneForDisplay';
import { getRoleDescription } from '../utils/getRoleDescription';

// Helper to identify if a role is an agent role
const isAgentRole = (role: ContactRoleType): boolean => {
  const roleName = role.name?.toLowerCase() || '';
  const spectoraType = role.spectoraContactType?.toLowerCase() || '';
  return (
    spectoraType === 'buyers agent' ||
    spectoraType === 'selling agent' ||
    roleName.includes('agent')
  );
};

// Helper to identify if a role is a client role (not an agent)
const isClientRole = (role: ContactRoleType): boolean => {
  // First check if it's an agent - if so, it's not a client
  if (isAgentRole(role)) {
    return false;
  }
  const roleName = role.name?.toLowerCase() || '';
  const spectoraType = role.spectoraContactType?.toLowerCase() || '';
  return spectoraType === 'buyer' || roleName.includes('client') || roleName.includes('buyer');
};

const emailSchema = z.string().email();

type ContactRoleSelectorProps = {
  role: ContactRoleType;
  contactsForRole: AdditionalContactSelection[];
  isPrimaryRole: boolean;
  selectedRole?: ContactRoleType | null;
};

export default function ContactRoleSelector({
  role,
  contactsForRole,
  isPrimaryRole,
  selectedRole,
}: ContactRoleSelectorProps) {
  const { setAdditionalContacts, jwtToken, companyId, companyStyles } = useScheduler();
  const [isActive, setIsActive] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const isEmailValid = useMemo(() => {
    if (!searchEmail) return false;
    return emailSchema.safeParse(searchEmail).success;
  }, [searchEmail]);

  const getIconComponent = (iconName: string) => {
    type IconKey = keyof typeof Icons;
    const IconComponent = Icons[iconName as IconKey] as
      | React.ComponentType<{ size?: number }>
      | undefined;
    return IconComponent ? <IconComponent size={20} /> : null;
  };

  const clearState = () => {
    setIsActive(false);
    setSearchEmail('');
    setShowForm(false);
    setCreateError(null);
  };

  const logDevContact = (data: ContactFormData | ContactType, isExisting: boolean) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn('🚨 DEVELOPMENT MODE: Contact submission skipped');
      console.log('Would have submitted additional contact:', {
        roleId: role._id,
        ...(isExisting
          ? {
              contact: {
                firstName: (data as ContactType).firstName,
                lastName: (data as ContactType).lastName,
                email: (data as ContactType).email,
                phone: (data as ContactType).phone,
              },
              isExisting: true,
            }
          : { ...data, isExisting: false }),
        submittedBy: 'external',
      });
    }
  };

  const handleEmailSearch = async () => {
    if (!isEmailValid) return;

    setIsSearching(true);

    try {
      const contact = await callServerAction<ContactType>({
        url: `contact/email/${encodeURIComponent(searchEmail)}`,
        method: 'get',
        params: {
          _companyId: companyId,
        },
        headers: {
          'client-token': jwtToken,
        },
      });

      if (contact) {
        logDevContact(contact, true);

        setAdditionalContacts((prev: AdditionalContactSelection[]) => [
          ...prev,
          { _roleId: role._id, _contactId: contact, primary: false },
        ]);

        clearState();
      }
    } catch (error) {
      // Contact not found - show form
      console.error('Error searching for contact:', error);
      setShowForm(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddContact = async (data: ContactFormData) => {
    setIsCreatingContact(true);
    setCreateError(null);

    try {
      logDevContact(data, false);

      // Format phone number to E.164 format before sending
      let formattedPhone: string | undefined = data.phone;
      if (data.phone && data.phone.trim()) {
        try {
          const phoneNumber = parsePhoneNumber(data.phone, 'US');
          if (phoneNumber && phoneNumber.isValid()) {
            formattedPhone = phoneNumber.format('E.164');
          } else {
            // If phone is invalid, still send it and let backend validation handle it
            formattedPhone = data.phone.trim();
          }
        } catch {
          // If parsing fails, send original value and let backend handle it
          formattedPhone = data.phone.trim();
        }
      }

      // Create contact via API
      const createdContact = await callServerAction<ContactType>({
        url: 'contact',
        method: 'post',
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: formattedPhone,
          submittedBy: 'external',
          preferredContactMethod: ['email', 'text'],
          _companyId: companyId,
        },
        params: {
          _companyId: companyId,
        },
        headers: {
          'client-token': jwtToken,
        },
      });

      if (!createdContact || !createdContact._id) {
        throw new Error('Failed to create contact - no contact returned');
      }

      // Use the created contact with real ID
      setAdditionalContacts((prev: AdditionalContactSelection[]) => [
        ...prev,
        { _roleId: role._id, _contactId: createdContact, primary: false },
      ]);

      clearState();
    } catch (error: any) {
      console.error('Error creating contact:', error);
      setCreateError(error?.message || 'Failed to create contact. Please try again.');
      // Don't clear state on error - let user try again
    } finally {
      setIsCreatingContact(false);
    }
  };

  const handleRemoveContact = (contactId: string) => {
    setAdditionalContacts((prev: AdditionalContactSelection[]) =>
      prev.filter(
        (contact) => !(contact._roleId === role._id && contact._contactId._id === contactId)
      )
    );
  };

  return (
    <div
      className={`rounded-lg border border-gray-300 bg-white p-4 ${isPrimaryRole ? 'bg-gray-50! border-dashed' : ''}`}
    >
      <div className='flex items-start gap-4'>
        {role.icon && (
          <div
            className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full'
            style={{
              backgroundColor: companyStyles.companyColor
                ? `${companyStyles.companyColor}1A`
                : 'rgba(0, 111, 238, 0.1)',
              color: companyStyles.companyColor || '#006FEE',
            }}
          >
            {getIconComponent(role.icon)}
          </div>
        )}
        <div className='flex-1'>
          <h3 className='text-base font-semibold text-gray-900'>
            {isPrimaryRole ? 'Additional' : ''} {role.name}
          </h3>
          {(() => {
            const desc = getRoleDescription(role.description);
            return desc ? <p className='mt-1 text-xs text-gray-600'>{desc}</p> : null;
          })()}

          {contactsForRole.length > 0 && !isActive ? (
            // Contacts Added - Display (when not actively adding)
            <div className='mt-3 space-y-2'>
              {contactsForRole.map((contact) => (
                <div
                  key={contact._contactId._id}
                  className='rounded-lg border border-success bg-success-50 p-3'
                >
                  <div className='flex items-center justify-between gap-3'>
                    <User
                      name={`${contact._contactId.firstName} ${contact._contactId.lastName}`}
                      description={
                        <div className='flex flex-col gap-0.5'>
                          <span>{contact._contactId.email}</span>
                          {contact._contactId.phone && (
                            <span className='text-xs'>
                              {formatPhoneForDisplay(contact._contactId.phone)}
                            </span>
                          )}
                        </div>
                      }
                      avatarProps={{
                        src: contact._contactId.thumb || contact._contactId.image,
                        size: 'sm',
                        showFallback: true,
                      }}
                    />
                    <Button
                      size='sm'
                      color='danger'
                      variant='flat'
                      isIconOnly
                      onPress={() => handleRemoveContact(contact._contactId._id)}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>
              ))}
              {/* Show "Add Another" button (all roles now support multiple contacts) */}
              <Button
                startContent={<FaPlus />}
                onPress={() => setIsActive(true)}
                className={`mt-2 ${companyStyles.secondaryButtonClassName}`}
                style={companyStyles.secondaryButtonStyles}
                variant='bordered'
              >
                Add Another {role.name}
              </Button>
            </div>
          ) : isActive ? (
            // Active - Show Form or Email Search (can be shown even if contacts exist)
            <div className='mt-3 space-y-3'>
              {/* Show existing contacts above the form when adding another */}
              {contactsForRole.length > 0 && (
                <div className='space-y-2'>
                  {contactsForRole.map((contact) => (
                    <div
                      key={contact._contactId._id}
                      className='rounded-lg border border-success bg-success-50 p-3'
                    >
                      <div className='flex items-center justify-between gap-3'>
                        <User
                          name={`${contact._contactId.firstName} ${contact._contactId.lastName}`}
                          description={
                            <div className='flex flex-col gap-0.5'>
                              <span>{contact._contactId.email}</span>
                              {contact._contactId.phone && (
                                <span className='text-xs'>
                                  {formatPhoneForDisplay(contact._contactId.phone)}
                                </span>
                              )}
                            </div>
                          }
                          avatarProps={{
                            src: contact._contactId.thumb || contact._contactId.image,
                            size: 'sm',
                            showFallback: true,
                          }}
                        />
                        <Button
                          size='sm'
                          color='danger'
                          variant='flat'
                          isIconOnly
                          onPress={() => handleRemoveContact(contact._contactId._id)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Active - Show Form or Email Search */}
              {!showForm ? (
                // Email Search Step
                <div className='space-y-3'>
                  <Input
                    type='email'
                    label='Email Address'
                    placeholder='your.email@example.com'
                    value={searchEmail}
                    onValueChange={setSearchEmail}
                    startContent={<FaEnvelope className='text-gray-400' />}
                    variant='bordered'
                    isRequired
                    classNames={{
                      inputWrapper: 'border bg-white shadow-none',
                      label: 'font-light uppercase text-xs',
                      input: 'text-base md:text-sm',
                    }}
                  />
                  <Button
                    type='button'
                    size='lg'
                    className={`w-full ${companyStyles.secondaryButtonClassName}`}
                    style={companyStyles.secondaryButtonStyles}
                    onPress={handleEmailSearch}
                    isLoading={isSearching}
                    isDisabled={!isEmailValid}
                    endContent={!isSearching && <FaArrowRight />}
                  >
                    {isSearching ? 'Searching...' : 'Search'}
                  </Button>
                </div>
              ) : (
                // Contact Not Found - Show Form
                <div className='space-y-3'>
                  <ContactQuickForm
                    key={`form-${role._id}`}
                    initialEmail={searchEmail}
                    onSubmit={handleAddContact}
                    onCancel={clearState}
                    isLoading={isCreatingContact}
                  />
                  {createError && (
                    <div className='rounded-lg border border-danger bg-danger-50 p-3 text-sm text-danger'>
                      {createError}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Inactive - Show Add Button
            <Button
              startContent={<FaPlus />}
              onPress={() => setIsActive(true)}
              className={`mt-3 ${companyStyles.secondaryButtonClassName}`}
              style={companyStyles.secondaryButtonStyles}
            >
              Add Contact
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
